/**
 * Time-phased execution planning engine (deterministic, pure).
 * Doc 102 §2A Future layer — consumes viability outputs, produces calendar placements.
 */

import { generateTimeBuckets, overlapDays, prorateByOverlap } from './planningEngine';
import type {
  LaunchProfile,
  LaunchProfilePhase,
  PlacementDemandChunk,
  PlacementStatus,
  TimePhasedEngineInput,
  TimePhasedEngineOutput,
  TimePhasedPlacement,
  TimePhasedPriorityInput,
  BucketUtilization,
  SupplyCalendarRow,
} from './timePhasedPlanning.types';
import {
  TIME_PHASED_ALGORITHM_VERSION,
  FALLBACK_LAUNCH_PROFILE,
  canonicalizeResourceType,
} from './timePhasedPlanning.types';

/** Calendar-day arithmetic in UTC — avoids CI/local TZ drift on ISO date strings. */
function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate.slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function weeksBetween(start: string, end: string): number {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return Math.round(ms / (7 * 24 * 60 * 60 * 1000));
}

function isBuildable(p: TimePhasedPriorityInput): boolean {
  if (!p.viabilityIncluded) return false;
  const blocked = ['Blocked', 'Not Ready', 'No Capacity Model', 'Blocked - Capability'];
  if (p.planDecision && blocked.some((b) => p.planDecision!.includes(b))) return false;
  return true;
}

function isLaunchable(p: TimePhasedPriorityInput, status: PlacementStatus): boolean {
  if (!isBuildable(p)) return false;
  if (p.participationAtRisk) return false;
  if (p.participationOverdue) return false;
  if (p.participationWindowMismatch) return false;
  if (p.participationCommitted === false) return false;
  const bad = ['Blocked - Capability', 'At Risk - Participation', 'At Risk - Capacity'];
  if (p.decisionWithParticipation && bad.includes(p.decisionWithParticipation)) return false;
  return status === 'scheduled' || status === 'slipped';
}

/** Expand workload template phases into demand chunks for a given launch anchor date. */
export function expandLaunchDemandAtAnchor(
  phases: LaunchProfilePhase[],
  launchAnchor: string,
  horizonStart: string,
  horizonEnd: string,
): PlacementDemandChunk[] {
  const chunks: PlacementDemandChunk[] = [];
  const buckets = generateTimeBuckets(horizonStart, horizonEnd, 'week');

  for (const phase of phases) {
    const phaseStart = addDays(launchAnchor, phase.offsetStartWeeks * 7);
    const phaseEnd = addDays(launchAnchor, phase.offsetEndWeeks * 7);
    const totalDays = Math.max(1, overlapDays(phaseStart, phaseEnd, phaseStart, phaseEnd));

    for (const bucket of buckets) {
      const od = overlapDays(phaseStart, phaseEnd, bucket.time_bucket_start, bucket.time_bucket_end);
      if (od <= 0) continue;
      const hours = prorateByOverlap(phase.hours, totalDays, od);
      if (hours <= 0) continue;
      chunks.push({
        resourceType: canonicalizeResourceType(phase.resourceType),
        bucketStart: bucket.time_bucket_start,
        bucketEnd: bucket.time_bucket_end,
        hours,
        phaseLabel: phase.label,
      });
    }
  }
  return chunks;
}

function supplyKey(resourceType: string, bucketStart: string): string {
  return `${resourceType}::${bucketStart}`;
}

function getSupplyHours(
  supply: Map<string, number>,
  resourceType: string,
  bucketStart: string,
  defaultHours: number,
): number {
  return supply.get(supplyKey(resourceType, bucketStart)) ?? defaultHours;
}

function canFitChunks(
  chunks: PlacementDemandChunk[],
  supply: Map<string, number>,
  defaultHours: number,
): boolean {
  for (const chunk of chunks) {
    const avail = getSupplyHours(supply, chunk.resourceType, chunk.bucketStart, defaultHours);
    if (avail < chunk.hours - 0.001) return false;
  }
  return true;
}

function consumeChunks(
  chunks: PlacementDemandChunk[],
  supply: Map<string, number>,
  defaultHours: number,
): void {
  for (const chunk of chunks) {
    const k = supplyKey(chunk.resourceType, chunk.bucketStart);
    const avail = supply.get(k) ?? defaultHours;
    supply.set(k, Math.max(0, avail - chunk.hours));
  }
}

function buildInitialSupply(
  supplyCalendar: SupplyCalendarRow[],
  horizonStart: string,
  horizonEnd: string,
  defaultHoursPerWeek: number,
): Map<string, number> {
  const map = new Map<string, number>();
  const buckets = generateTimeBuckets(horizonStart, horizonEnd, 'week');
  const resourceTypes = new Set(supplyCalendar.map((r) => r.resourceType));

  for (const row of supplyCalendar) {
    map.set(
      supplyKey(canonicalizeResourceType(row.resourceType), row.bucketStart),
      row.supplyHours,
    );
  }
  for (const rt of resourceTypes) {
    const canon = canonicalizeResourceType(rt);
    for (const b of buckets) {
      const k = supplyKey(canon, b.time_bucket_start);
      if (!map.has(k)) map.set(k, defaultHoursPerWeek);
    }
  }
  return map;
}

/** Scale fallback phases by Item Effort / priority CU when present. */
export function scaleFallbackProfile(effortCapacityHours?: number): LaunchProfile {
  const base = FALLBACK_LAUNCH_PROFILE;
  const baseTotal = base.phases.reduce((s, p) => s + p.hours, 0);
  if (!effortCapacityHours || effortCapacityHours <= 0 || baseTotal <= 0) return base;
  // Treat effort hours as primary eng demand; keep GTM phases proportional.
  const scale = Math.max(0.25, Math.min(3, effortCapacityHours / 240));
  return {
    ...base,
    name: `${base.name} (scaled ×${scale.toFixed(2)})`,
    phases: base.phases.map((p) => ({
      ...p,
      resourceType: canonicalizeResourceType(p.resourceType),
      hours: Math.round(p.hours * scale),
    })),
  };
}

function placePriority(
  priority: TimePhasedPriorityInput,
  profile: LaunchProfile | undefined,
  supply: Map<string, number>,
  input: TimePhasedEngineInput,
): { placement: TimePhasedPlacement; consumed: PlacementDemandChunk[] } {
  const empty = (status: PlacementStatus, rationale: Record<string, unknown>): TimePhasedPlacement => ({
    priorityId: priority.id,
    priorityName: priority.name,
    owningOrg: priority.owningOrg,
    targetLaunchStart: priority.targetLaunchStart,
    targetLaunchEnd: priority.targetLaunchEnd,
    placementStatus: status,
    buildable: isBuildable(priority),
    launchable: false,
    rationale,
  });

  if (!priority.viabilityIncluded) {
    return { placement: empty('below_line', { reason: 'Below viability line' }), consumed: [] };
  }

  // No profile assigned → place with effort-scaled fallback rather than dead-ending.
  const assumedProfile = !profile?.phases?.length;
  const effectiveProfile = assumedProfile
    ? scaleFallbackProfile(priority.effortCapacityHours)
    : {
        ...profile!,
        phases: profile!.phases.map((p) => ({
          ...p,
          resourceType: canonicalizeResourceType(p.resourceType),
        })),
      };

  const defaultHours = input.defaultHoursPerWeek ?? 40;
  const buckets = generateTimeBuckets(input.horizonStart, input.horizonEnd, 'week');
  const flexibility =
    priority.anchoring ?? priority.launchFlexibility ?? 'flexible';
  const slackWeeks = priority.slackWeeks ?? 0;
  const minStart = priority.targetLaunchStart ?? input.horizonStart;
  // Fixed = immovable: only anchors inside the target window (or single start week).
  // Flexible = may slide later; soft-slip band uses slackWeeks before hard "slipped".
  const maxAnchor =
    flexibility === 'fixed'
      ? (priority.targetLaunchEnd ?? priority.targetLaunchStart ?? input.horizonEnd)
      : input.horizonEnd;
  const candidates = buckets.filter(
    (b) => b.time_bucket_start >= minStart && b.time_bucket_start <= maxAnchor,
  );
  const impactedTeams = [
    ...new Set(effectiveProfile.phases.map((p) => canonicalizeResourceType(p.resourceType))),
  ];

  for (const bucket of candidates) {
    const anchor = bucket.time_bucket_start;
    const chunks = expandLaunchDemandAtAnchor(
      effectiveProfile.phases,
      anchor,
      input.horizonStart,
      input.horizonEnd,
    );
    if (!canFitChunks(chunks, supply, defaultHours)) continue;

    const endDates = chunks.map((c) => c.bucketEnd);
    const feasibleEnd = endDates.length ? endDates.sort().at(-1)! : anchor;

    let status: PlacementStatus = 'scheduled';
    let slipWeeks: number | undefined;
    if (priority.targetLaunchEnd && anchor > priority.targetLaunchEnd) {
      const rawSlip = weeksBetween(priority.targetLaunchEnd, anchor);
      // Flexible work: soft band inside slackWeeks still counts as scheduled.
      if (flexibility === 'flexible' && rawSlip <= slackWeeks) {
        status = 'scheduled';
        slipWeeks = rawSlip > 0 ? rawSlip : undefined;
      } else {
        status = 'slipped';
        slipWeeks = rawSlip;
      }
    }

    const baseConfidence = priority.calendarConfidence ?? 0.75;
    const confidence =
      status === 'slipped'
        ? Math.max(0.15, baseConfidence - 0.25)
        : assumedProfile
          ? Math.max(0.2, baseConfidence - 0.15)
          : baseConfidence;

    const placement: TimePhasedPlacement = {
      priorityId: priority.id,
      priorityName: priority.name,
      owningOrg: priority.owningOrg,
      feasibleStart: anchor,
      feasibleEnd,
      targetLaunchStart: priority.targetLaunchStart,
      targetLaunchEnd: priority.targetLaunchEnd,
      slipWeeks,
      placementStatus: status,
      buildable: isBuildable(priority),
      launchable: isLaunchable(priority, status),
      assumedProfile,
      launchFlexibility: flexibility,
      anchoring: flexibility,
      calendarConfidence: Math.round(confidence * 100) / 100,
      slackWeeks,
      commitmentFirmness: priority.commitmentFirmness,
      impactedTeams,
      rationale: {
        profile: effectiveProfile.name,
        phases: effectiveProfile.phases.map((p) => p.label),
        teams: impactedTeams,
        launchFlexibility: flexibility,
        anchoring: flexibility,
        commitmentFirmness: priority.commitmentFirmness,
        ...(assumedProfile
          ? {
              assumedProfile: true,
              effortCapacityHours: priority.effortCapacityHours ?? null,
            }
          : {}),
      },
    };
    return { placement, consumed: chunks };
  }

  return {
    placement: {
      ...empty('supply_exhausted', {
        reason:
          flexibility === 'fixed'
            ? 'Fixed launch window has no week with sufficient shared-team capacity (will not slide)'
            : 'No calendar window with sufficient shared-team capacity',
        teams: impactedTeams,
        launchFlexibility: flexibility,
        ...(assumedProfile ? { assumedProfile: true } : {}),
      }),
      assumedProfile,
      launchFlexibility: flexibility,
      calendarConfidence: Math.max(0.1, (priority.calendarConfidence ?? 0.75) - 0.4),
      slackWeeks,
      impactedTeams,
    },
    consumed: [],
  };
}

export function runTimePhasedPlacementEngine(input: TimePhasedEngineInput): TimePhasedEngineOutput {
  const defaultHours = input.defaultHoursPerWeek ?? 40;
  const supply = buildInitialSupply(
    input.supplyCalendar,
    input.horizonStart,
    input.horizonEnd,
    defaultHours,
  );

  // Force rank first; within rank, firmer commitments place/keep before softer (Slice 4).
  // Soft types are first candidates to slide when supply is contested.
  const sorted = [...input.priorities].sort((a, b) => {
    const rank = (a.forceRank ?? 9999) - (b.forceRank ?? 9999);
    if (rank !== 0) return rank;
    const firmA =
      (a.anchoring ?? a.launchFlexibility) === 'fixed'
        ? 10_000
        : (a.commitmentFirmness ?? 4);
    const firmB =
      (b.anchoring ?? b.launchFlexibility) === 'fixed'
        ? 10_000
        : (b.commitmentFirmness ?? 4);
    return firmB - firmA;
  });
  const placements: TimePhasedPlacement[] = [];
  const demandByBucket = new Map<string, number>();

  for (const priority of sorted) {
    const { placement, consumed } = placePriority(priority, priority.launchProfile, supply, input);
    placements.push(placement);
    if (consumed.length > 0) {
      consumeChunks(consumed, supply, defaultHours);
      for (const chunk of consumed) {
        const k = supplyKey(chunk.resourceType, chunk.bucketStart);
        demandByBucket.set(k, (demandByBucket.get(k) ?? 0) + chunk.hours);
      }
    }
  }

  const buckets = generateTimeBuckets(input.horizonStart, input.horizonEnd, 'week');
  const anyAssumed = input.priorities.some((p) => !p.launchProfile?.phases?.length);
  const resourceTypes = new Set<string>([
    ...input.supplyCalendar.map((r) => r.resourceType),
    ...input.priorities.flatMap((p) => p.launchProfile?.phases.map((ph) => ph.resourceType) ?? []),
    ...(anyAssumed ? FALLBACK_LAUNCH_PROFILE.phases.map((ph) => ph.resourceType) : []),
  ]);

  const bucketUtilization: BucketUtilization[] = [];
  for (const rt of resourceTypes) {
    for (const b of buckets) {
      const k = supplyKey(rt, b.time_bucket_start);
      const supplyH =
        input.supplyCalendar.find((r) => r.resourceType === rt && r.bucketStart === b.time_bucket_start)
          ?.supplyHours ?? defaultHours;
      const demandH = demandByBucket.get(k) ?? 0;
      bucketUtilization.push({
        resourceType: rt,
        bucketStart: b.time_bucket_start,
        bucketEnd: b.time_bucket_end,
        supplyHours: supplyH,
        demandHours: demandH,
        utilizationPct: supplyH > 0 ? Math.round((demandH / supplyH) * 100) : 0,
      });
    }
  }

  return {
    algorithmVersion: TIME_PHASED_ALGORITHM_VERSION,
    placements,
    bucketUtilization,
    summary: {
      scheduled: placements.filter((p) => p.placementStatus === 'scheduled').length,
      slipped: placements.filter((p) => p.placementStatus === 'slipped').length,
      blocked: placements.filter((p) => p.placementStatus === 'blocked').length,
      unscheduled: placements.filter((p) => p.placementStatus === 'unscheduled').length,
      supplyExhausted: placements.filter((p) => p.placementStatus === 'supply_exhausted').length,
      belowLine: placements.filter((p) => p.placementStatus === 'below_line').length,
      total: placements.length,
    },
  };
}

export function applyPromoteLever(
  priorities: TimePhasedPriorityInput[],
  priorityId: string,
): TimePhasedPriorityInput[] {
  const minRank = Math.min(...priorities.map((p) => p.forceRank ?? 9999));
  return priorities.map((p) =>
    p.id === priorityId ? { ...p, forceRank: minRank - 1 } : p,
  );
}

export function applyDelayLever(
  priorities: TimePhasedPriorityInput[],
  priorityId: string,
  delayWeeks: number,
): TimePhasedPriorityInput[] {
  return priorities.map((p) => {
    if (p.id !== priorityId) return p;
    // Fixed (hard) anchors are immovable — delay lever is a no-op.
    if ((p.anchoring ?? p.launchFlexibility ?? 'flexible') === 'fixed') return p;
    return {
      ...p,
      targetLaunchStart: p.targetLaunchStart
        ? addDays(p.targetLaunchStart, delayWeeks * 7)
        : undefined,
      targetLaunchEnd: p.targetLaunchEnd
        ? addDays(p.targetLaunchEnd, delayWeeks * 7)
        : undefined,
    };
  });
}
